import { randomUUID } from 'node:crypto';
import {
  isHttpError,
  createHttpErrorFromFetch,
  isHttpApplicationError,
} from '@wix/cli-http-client';
import type { HttpClient } from '@wix/cli-http-client';
import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import {
  createAppDeployment,
  completeAppDeployment,
} from '@wix/ambassador-velo-backend-v1-app-deployment/http';
import type {
  StaticFileMetadata,
  AppDeployment,
} from '@wix/ambassador-velo-backend-v1-app-deployment/types';
import {
  createAppProject,
  getAppProject,
} from '@wix/ambassador-velo-backend-v1-app-project/http';
import type { CloudProvider } from '@wix/ambassador-velo-backend-v1-app-project/types';
import {
  deleteAppEnvironmentVariableByEnvironmentAndKey,
  bulkUpsertAppEnvironmentVariables,
  getAppEnvironmentVariablesByEnvironment,
} from '@wix/ambassador-velo-backend-v2-app-environment-variable/http';
import { Mutability } from '@wix/ambassador-velo-backend-v2-app-environment-variable/types';
import type { BackendFile } from './schemas.js';
import {
  AppProjectSchema,
  CompleteAppDeploymentSchema,
  CreateAppDeploymentSchema,
  CreateAppProjectSchema,
  GetAppEnvironmentVariablesSchema,
  StaticFileUploadSchema,
  UpsertAppEnvironmentVariablesSchema,
} from './schemas.js';

const { minRetryTimeout } = getTestOverrides();

const APP_PROJECT_TYPE_ID = '79144b61-68b9-4b0b-bfa7-e3adfa610101';
const HEADLESS_PROJECT_TYPE_ID = 'eb363dea-85a0-4159-9b05-949542be5079';

export class BackendAsAServiceClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  getAppProject = async (appId: string) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getAppProject({ appProjectId: appId })),
        this.retryOptions
      );
      return AppProjectSchema.parse(data).appProject;
    } catch (e) {
      if (isHttpError(e) && e.response?.status === 404) {
        return;
      }
      throw new CliError({
        code: CliErrorCode.FailedToGetAppProject(),
        cause: e,
      });
    }
  };

  createAppProject = async ({
    cloudProvider,
    projectType,
    ...appProjectOptions
  }: {
    id: string;
    displayName: string;
    slug: string;
    cloudProvider?: CloudProvider;
    projectType?: 'APP' | 'HEADLESS';
  }) => {
    try {
      const { data } = await this.httpClient.request(
        createAppProject({
          appProject: {
            ...appProjectOptions,
            appProjectTypeId:
              projectType === 'HEADLESS'
                ? HEADLESS_PROJECT_TYPE_ID
                : APP_PROJECT_TYPE_ID,
            cloudProviderOverride: cloudProvider,
          },
        })
      );

      return CreateAppProjectSchema.parse(data).appProject;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedCreatingAppProject(),
        cause: e,
      });
    }
  };

  createAppDeployment = async (
    appId: string,
    files: BackendFile[] | undefined,
    staticFilesMetadata: StaticFileMetadata[] | undefined,
    generateAppDeploymentGroupId?: boolean
  ) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            createAppDeployment({
              appDeployment: {
                appProjectId: appId,
                files,
                staticFilesMetadata,
                appDeploymentGroupId: generateAppDeploymentGroupId
                  ? randomUUID()
                  : undefined,
              },
            })
          ),
        this.retryOptions
      );

      return CreateAppDeploymentSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedCreatingAppDeployment(),
        cause: e,
      });
    }
  };

  completeAppDeployment = async (
    appDeployment: AppDeployment,
    staticsCompletionToken?: string
  ) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            completeAppDeployment({
              appDeployment,
              staticsCompletionToken,
            })
          ),
        this.retryOptions
      );

      return CompleteAppDeploymentSchema.parse(data).appDeployment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedFinalizingAppDeployment(),
        cause: e,
      });
    }
  };

  postForm = async (endpointUrl: string, token: string, formData: FormData) => {
    try {
      const responseJson = await pRetry(
        async () => {
          const response = await fetch(endpointUrl, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });

          if (!response.ok) {
            const error = await createHttpErrorFromFetch(response);
            throw error;
          }

          return response.json();
        },
        { retries: 3 }
      );

      return StaticFileUploadSchema.parse(responseJson);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUploadStaticFiles(),
        cause: e,
        info: { endpointUrl },
      });
    }
  };

  putBinaryFile = async (
    endpointUrl: string,
    fileContent: Buffer,
    contentType: string
  ) => {
    try {
      await pRetry(
        async () => {
          const response = await fetch(endpointUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': contentType,
            },
            body: fileContent,
          });

          if (!response.ok) {
            const error = await createHttpErrorFromFetch(response);
            throw error;
          }
        },
        { retries: 3 }
      );
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUploadStaticFiles(),
        cause: e,
        info: { endpointUrl },
      });
    }
  };

  // Env management

  getAppEnvironmentVariables = async (appId: string, environment: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            getAppEnvironmentVariablesByEnvironment({
              appProjectId: appId,
              environment,
            })
          ),
        this.retryOptions
      );

      const { appEnvironmentVariables } =
        GetAppEnvironmentVariablesSchema.parse(data);

      return appEnvironmentVariables;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetAppEnvironmentVariablesByEnvironment(),
        cause: e,
      });
    }
  };

  upsertAppEnvironmentVariables = async (
    appId: string,
    environment: string,
    variables: Record<string, string>
  ) => {
    let result;

    try {
      const { data } = await this.httpClient.request(
        bulkUpsertAppEnvironmentVariables({
          appProjectId: appId,
          environment,
          variables,
          mutability: Mutability.STATIC,
          returnEntity: true,
          returnAllEnvironment: true,
        })
      );

      result = UpsertAppEnvironmentVariablesSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToBulkUpsertAppEnvironmentVariables(),
        cause: e,
      });
    }

    const { bulkActionMetadata, appEnvironmentVariables } = result;

    if (
      bulkActionMetadata?.totalFailures ||
      bulkActionMetadata?.undetailedFailures
    ) {
      throw new CliError({
        code: CliErrorCode.FailedToBulkUpsertAppEnvironmentVariables(),
        cause: undefined,
        info: { ...result },
      });
    }

    return appEnvironmentVariables;
  };

  deleteAppEnvironmentVariable = async (
    appId: string,
    environment: string,
    variable: string
  ) => {
    try {
      await this.httpClient.request(
        deleteAppEnvironmentVariableByEnvironmentAndKey({
          appProjectId: appId,
          environment,
          key: variable,
        })
      );
    } catch (e) {
      if (isHttpError(e) && isHttpApplicationError(e)) {
        const applicationError = e.response?.data.details.applicationError;
        if (applicationError?.code === 'APP_ENVIRONMENT_VARIABLE_NOT_FOUND') {
          return;
        }
      }

      throw new CliError({
        code: CliErrorCode.FailedToDeleteAppEnvironmentVariableByEnvironmentAndKey(),
        cause: e,
      });
    }
  };
}
